import {
  ArrowLeftOutlined,
  AudioOutlined,
  CaretRightFilled,
  ClockCircleOutlined,
  FileTextOutlined,
  MenuOutlined,
  RightOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Collapse, Drawer } from "antd";
import { type ReactNode, memo, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import styles from "./tutorial.module.scss";

const CONSENT_FORM_URL = "/pdf/client_consent_form.pdf";

// Real "Start A Session" plus-circle icon (lifted from RecordCTA) so the mini
// chip in the instructions maps 1:1 to the actual left-panel control.
const StartPlusIcon = ({ size = 19 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ display: "block" }}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z"
    />
  </svg>
);

// Reused from the previous tutorial: lazy, muted, auto-playing step video.
const VideoPlayer = memo(({ src }: { src: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobilePath = useAppSelector((state) => state.appState.isMobilePath);
  const [_isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isMobilePath) {
      return;
    }
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting && videoRef.current) {
            setTimeout(() => {
              videoRef.current?.play().catch(() => {
                // Handle play error silently
              });
            }, 1500);
          } else if (videoRef.current) {
            videoRef.current.pause();
          }
        },
        { threshold: 0.5 },
      );
      if (videoRef.current) {
        observer.observe(videoRef.current);
      }
      return () => {
        if (videoRef.current) {
          observer.unobserve(videoRef.current);
        }
      };
    }
  }, [isMobilePath]);

  return <video ref={videoRef} src={src} loop muted playsInline controls className={styles.tutorial__video} />;
});

VideoPlayer.displayName = "VideoPlayer";

// Faithful mini of the real "Start A Session" / "Start Encounter" button.
const StartButtonChip = ({ label }: { label: string }) => (
  <span className={styles.tutorial__startChip}>
    <span className={styles.tutorial__startChip_icon}>
      <StartPlusIcon size={19} />
    </span>
    <span className={styles.tutorial__startChip_label}>{label}</span>
  </span>
);

type StepTarget =
  | { kind: "left" }
  | { kind: "button"; caption: string; label: string }
  | { kind: "note"; caption: string };

// Points to the real, permanent control for each step (not a new button).
const RealTarget = ({ target, startLabel }: { target: StepTarget; startLabel: string }) => {
  if (target.kind === "left") {
    return (
      <div className={styles.tutorial__targetLeft}>
        <span className={styles.tutorial__targetLeft_arrow}>
          <ArrowLeftOutlined />
        </span>
        <span>
          Click <StartButtonChip label={startLabel} /> in the panel on your left
        </span>
      </div>
    );
  }
  return (
    <div className={styles.tutorial__targetBox}>
      <span className={styles.tutorial__targetBox_caption}>{target.caption}</span>
      {target.kind === "button" ? (
        <span className={styles.tutorial__targetBox_button}>{target.label}</span>
      ) : (
        <span className={styles.tutorial__noteRow}>
          <span className={styles.tutorial__noteRow_avatar}>JA</span>
          <span>
            <span className={styles.tutorial__noteRow_title}>Progress Note — Jordan A.</span>
            <span className={styles.tutorial__noteRow_meta}>Just now · SOAP</span>
          </span>
        </span>
      )}
    </div>
  );
};

// Compact, non-interactive references to the drawer's first two questions —
// deliberately plain text so clinicians know the picks happen inside the drawer.
const DocumentTypeSnapshot = () => (
  <div className={styles.tutorial__snapshot}>
    <FileTextOutlined className={styles.tutorial__snapshot_icon} />
    <span>
      In the drawer, you'll first <strong>choose a document type</strong> — SOAP, DAP, Intake, EMDR and more.
    </span>
  </div>
);

const ChooseModeSnapshot = () => {
  const { sessionTerm } = useSpecialityReference();
  return (
    <div className={styles.tutorial__snapshot}>
      <AudioOutlined className={styles.tutorial__snapshot_icon} />
      <span>
        Then <strong>choose how to capture</strong> — Live {sessionTerm}, Dictate, Upload, or Describe.
      </span>
    </div>
  );
};

const ConsentStrip = () => (
  <div className={styles.tutorial__consent}>
    <span className={styles.tutorial__consent_icon}>
      <SafetyOutlined />
    </span>
    <span className={styles.tutorial__consent_text}>
      <strong>Patient consent:</strong> share the form before your first session. Find it any time in the menu{" "}
      <span className={styles.tutorial__consent_menu}>
        <MenuOutlined />
      </span>{" "}
      → <strong className={styles.tutorial__consent_link}>Consent Form</strong>.
    </span>
    <a className={styles.tutorial__ghostButton} href={CONSENT_FORM_URL} target="_blank" rel="noreferrer">
      <FileTextOutlined />
      Open Consent Form
    </a>
  </div>
);

const SAMPLE_SECTIONS: [string, string][] = [
  [
    "Subjective",
    "Established patient (35yo, professional) returns for six-week follow-up of generalized anxiety disorder on sertraline 50 mg daily, initiated at last visit. Reports subjective improvement in baseline worry and reactivity (“more even-keel during the workday”) and denies medication side effects — no nausea, sexual dysfunction, headache, or worsened sleep. Sleep-onset latency improved from ~45 minutes to ~20 minutes on four of seven nights; intermittent early-morning awakenings around 4:30am with ruminative content tied to a Q4 work deadline. Denies suicidal ideation, homicidal ideation, self-harm urges, and hopelessness; no plan, intent, or means. GAD-7 administered in session: 9 (mild), down from 16 at intake six weeks ago. Continues weekly individual psychotherapy with outside therapist.",
  ],
  [
    "Objective",
    "Joined telehealth on time from a home office; appropriately dressed and groomed. Speech normal in rate, volume, and prosody. Mood self-reported as “better but still on edge before deadlines”; affect mildly anxious, congruent, and full in range. Thought process linear and goal-directed; thought content denies SI/HI, auditory or visual hallucinations, paranoid ideation, and obsessions. Insight and judgment good. No abnormal involuntary movements observed.",
  ],
  [
    "Assessment",
    "Generalized Anxiety Disorder (F41.1), moderate — clinically improved on sertraline 50 mg daily with concurrent psychotherapy (GAD-7 16 → 9 over six weeks). Tolerating medication without side effects. No current evidence of comorbid major depressive disorder, panic disorder, or substance use disorder. No acute safety concerns; protective factors include treatment engagement, stable employment and housing, intact social support, and absence of access to lethal means.",
  ],
  [
    "Plan",
    "Continue sertraline 50 mg daily; reassess in four weeks for dose adjustment vs. maintenance. Approximately 18 minutes of supportive psychotherapy provided in session, distinct from the E/M portion of the visit, focused on cognitive restructuring of catastrophic predictions about work performance and reinforcement of stimulus-control sleep techniques. Encouraged continuation of weekly psychotherapy with outside therapist for skills consolidation. Caffeine ceiling reaffirmed (≤2 cups/day before noon); sleep log to be continued. Re-administer GAD-7 at next visit. Patient verbalized understanding and agreed to call if symptoms worsen or new side effects emerge. Follow-up scheduled in four weeks.",
  ],
];

type SampleProceduralCode = {
  code: string;
  shortDescription?: string;
  psychotherapyMinutes?: number;
  explanation: string;
};

const SAMPLE_PROCEDURAL_CODES: SampleProceduralCode[] = [
  {
    code: "99214",
    shortDescription: "Office/outpatient visit, established patient, moderate medical decision-making (20–29 min)",
    explanation:
      "Moderate medical decision-making driven by an established chronic illness with ongoing pharmacotherapy management (sertraline 50 mg for GAD), review of symptom trend data (GAD-7 16 → 9), and assessment of medication tolerability. Visit met history, problem-focused exam, and moderate-complexity MDM components.",
  },
  {
    code: "90833",
    psychotherapyMinutes: 18,
    explanation:
      "Approximately 18 minutes of supportive psychotherapy provided distinct from the evaluation and management portion of the visit, addressing cognitive restructuring of catastrophic predictions about work performance and reinforcement of stimulus-control sleep techniques. Time documented separately per 90833 requirements (16–37 minute range).",
  },
];

type SampleDiagnosticCode = {
  code: string;
  description: string;
};

const SAMPLE_DIAGNOSTIC_CODES: SampleDiagnosticCode[] = [
  {
    code: "F41.1",
    description: "Generalized anxiety disorder",
  },
];

const SampleNoteDrawer = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <Drawer
    open={open}
    onClose={onClose}
    placement="right"
    width="min(540px, 94vw)"
    title={
      <div>
        <div className={styles.tutorial__sample_eyebrow}>Sample note</div>
        <div className={styles.tutorial__sample_heading}>What Nudge writes for you</div>
      </div>
    }
    styles={{ body: { padding: 24 } }}
  >
    <div className={styles.tutorial__sample_callout}>
      <span className={styles.tutorial__sample_callout_icon}>
        <ThunderboltOutlined />
      </span>
      <span>
        Generated from a 30-min visit in <strong>under 30 seconds</strong>. Fully customizable.
      </span>
    </div>
    <div className={styles.tutorial__sample_title}>
      <span className={styles.tutorial__sample_title_text}>Psychiatric Follow-up</span>
      <span className={styles.tutorial__sample_title_tag}>SOAP</span>
    </div>
    <div className={styles.tutorial__sample_meta}>Jordan A. · 30 min · Med management + brief psychotherapy</div>
    {SAMPLE_SECTIONS.map(([heading, body]) => (
      <div className={styles.tutorial__sample_section} key={heading}>
        <div className={styles.tutorial__sample_section_head}>{heading}</div>
        <p className={styles.tutorial__sample_section_body}>{body}</p>
      </div>
    ))}

    {/* Procedural codes — mirrors the production ProceduralCoding card */}
    <div className={styles.tutorial__sample_section}>
      <div className={styles.tutorial__sample_section_head}>Procedural Codes</div>
      {SAMPLE_PROCEDURAL_CODES.map((code, idx) => (
        <div
          key={code.code}
          style={{
            borderTop: idx > 0 ? "1px solid #f0f0f0" : "none",
            paddingTop: idx > 0 ? 12 : 8,
            marginTop: idx > 0 ? 12 : 0,
          }}
        >
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <strong>CPT Code:</strong> {code.code}
          </div>
          {code.shortDescription && (
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Short Description:</strong> {code.shortDescription}
            </div>
          )}
          {code.psychotherapyMinutes !== undefined && (
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Psychotherapy mins:</strong> {code.psychotherapyMinutes}
            </div>
          )}
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Explanation:</div>
            <p className={styles.tutorial__sample_section_body}>{code.explanation}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Diagnostic codes — mirrors the production DiagnosticCoding card */}
    <div className={styles.tutorial__sample_section}>
      <div className={styles.tutorial__sample_section_head}>Diagnostic Codes (ICD-10)</div>
      {SAMPLE_DIAGNOSTIC_CODES.map((code, idx) => (
        <div
          key={code.code}
          style={{
            borderTop: idx > 0 ? "1px solid #f0f0f0" : "none",
            paddingTop: idx > 0 ? 12 : 8,
            marginTop: idx > 0 ? 12 : 0,
          }}
        >
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <strong>ICD-10 Code:</strong> {code.code}
          </div>
          <div style={{ fontSize: 13 }}>
            <strong>Description:</strong> {code.description}
          </div>
        </div>
      ))}
    </div>
  </Drawer>
);

type Step = {
  key: string;
  tag: string;
  duration: string;
  title: string;
  body: ReactNode;
  target: StepTarget;
  showDoc?: boolean;
  showMode?: boolean;
  sample?: boolean;
  videoUrl: string;
};

const Tutorial = () => {
  const { referIndividualBy, shouldReferAsPatient, sessionTerm, sessionTermLower } = useSpecialityReference();
  const [activeKeys, setActiveKeys] = useState<string[]>(["1"]);
  const [sampleOpen, setSampleOpen] = useState(false);

  const startLabel = shouldReferAsPatient ? "Start Encounter" : "Start A Session";
  const personWord = referIndividualBy.toLowerCase();
  const drawerButton = `Start ${sessionTerm}`;

  const steps: Step[] = [
    {
      key: "1",
      tag: "Create",
      duration: "0:02",
      title: `Start ${shouldReferAsPatient ? "an" : "a"} ${sessionTermLower}`,
      body: "Everything starts here — the button is in the panel on your left. Clicking it opens the note drawer.",
      target: { kind: "left" },
      videoUrl: "/assets/tutorial-step-1.mp4",
    },
    {
      key: "2",
      tag: "Set up",
      duration: "0:08",
      title: "Make your picks in the drawer",
      body: `The drawer asks a few quick choices — the ${personWord} and note details too — then you begin the ${sessionTermLower}:`,
      target: { kind: "button", caption: "Then click", label: drawerButton },
      showDoc: true,
      showMode: true,
      videoUrl: "/assets/tutorial-step-2.mp4",
    },
    {
      key: "3",
      tag: "Review",
      duration: "0:08",
      title: "Review your note",
      body: "Your note generates in seconds and appears in this Notes list. Click it to read, edit, and finalize.",
      target: { kind: "note", caption: "Then open it here:" },
      sample: true,
      videoUrl: "/assets/tutorial-step-3.mp4",
    },
  ];

  const renderHeader = (step: Step, isOpen: boolean) => (
    <div className={styles.tutorial__stepHeader}>
      <span className={`${styles.tutorial__stepNum} ${isOpen ? styles.tutorial__stepNum_active : ""}`}>{step.key}</span>
      <span className={styles.tutorial__stepHeading}>
        <span className={styles.tutorial__stepTag}>{step.tag}</span>
        <span className={styles.tutorial__stepTitle}>{step.title}</span>
      </span>
      <span className={styles.tutorial__videoChip}>
        <CaretRightFilled className={styles.tutorial__videoChip_play} />
        <span className={styles.tutorial__videoChip_label}>Video · </span>
        {step.duration}
      </span>
    </div>
  );

  return (
    <div className={styles.tutorial__card}>
      <div className={styles.tutorial__inner}>
        {/* Welcome */}
        <div className={styles.tutorial__welcome}>
          <div className={styles.tutorial__welcome_row}>
            <span className={styles.tutorial__pill}>
              <ClockCircleOutlined />
              Your first note in about 2 minutes
            </span>
            <a className={styles.tutorial__ghostButton} href={CONSENT_FORM_URL} target="_blank" rel="noreferrer">
              <FileTextOutlined />
              Open Consent Form
            </a>
          </div>
          <h1 className={styles.tutorial__title}>Welcome to your Clinic, on Nudge — let's write your first note</h1>
          <p className={styles.tutorial__subtitle}>Three steps to your first note — each takes under a minute.</p>
        </div>

        {/* Steps */}
        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <RightOutlined rotate={isActive ? 90 : 0} className={styles.tutorial__chevron} />
          )}
          className={styles.tutorial__steps}
          items={steps.map((step) => ({
            key: step.key,
            label: renderHeader(step, activeKeys.includes(step.key)),
            className: styles.tutorial__stepPanel,
            children: (
              <div className={styles.tutorial__stepBody}>
                <div className={styles.tutorial__stepBody_main}>
                  <p className={styles.tutorial__stepDesc}>{step.body}</p>
                  {step.showDoc && <DocumentTypeSnapshot />}
                  {step.showMode && <ChooseModeSnapshot />}
                  <div className={styles.tutorial__targetWrap}>
                    <RealTarget target={step.target} startLabel={startLabel} />
                  </div>
                  {step.sample && (
                    <button type="button" className={styles.tutorial__sampleLink} onClick={() => setSampleOpen(true)}>
                      See a finished sample note
                      <RightOutlined />
                    </button>
                  )}
                </div>
                <div className={styles.tutorial__stepMedia}>
                  <VideoPlayer src={step.videoUrl} />
                </div>
              </div>
            ),
          }))}
        />

        {/* Consent strip */}
        <ConsentStrip />
      </div>

      <SampleNoteDrawer open={sampleOpen} onClose={() => setSampleOpen(false)} />
    </div>
  );
};

export default Tutorial;
